import React from 'react'
import { connect } from 'react-redux'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import * as S from '../CarecoAdminDashboard/CarecoAdminStyle'
import { UserIcon, RightIcon, ManagerIcon, CarerIcon, AdminIcon, ServiceUserIcon } from 'uiKit/icons/Icons'
import LabelWithTooltip from 'uiKit/LabelWithTooltip/LabelWithTooltip'
import { isProfessionalsNavAllowed, isServiceUsersNavAllowed } from 'tabs/home/api/usageLimits'

type OverviewData = {
  totalUsers?: number
  totalManagers?: number
  totalCarers?: number
  totalAdmins?: number
  totalServiceUsers?: number
} | null

interface OverviewProps extends RouteComponentProps {
  data?: OverviewData
  activeBot?: any
}

const Overview: React.FC<OverviewProps> = ({ history, match, data, activeBot }) => {
  const totals = {
    users: data?.totalUsers ?? 0,
    managers: data?.totalManagers ?? 0,
    carers: data?.totalCarers ?? 0,
    admins: data?.totalAdmins ?? 0,
    serviceUsers: data?.totalServiceUsers ?? 0,
  }
  const showManagersAndCarers = isProfessionalsNavAllowed(activeBot)
  // Supervision off (and never used) → only admins are listed, so the header counts admins.
  const totalUsersCount = showManagersAndCarers ? totals.users : totals.admins
  const showServiceUsers = isServiceUsersNavAllowed(activeBot)

  const handleNavigation = () => {
    const baseUrl = (match as any)?.url || ''
    history.push(`${baseUrl}/total-users`)
  }

  return (
    <S.Container>
      <S.SectionTitle>Overview</S.SectionTitle>
      <S.OverviewCards>
      <S.Card>
        <S.HeaderRow>
          <S.HeaderLeft>
            <S.IconCircle>
              <UserIcon />
            </S.IconCircle>
            <S.CardSubtitle>
              Total users: {totalUsersCount}
            </S.CardSubtitle>
            <LabelWithTooltip
              text=""
              tooltipId="overview-total-users"

              tooltipText="Total number of people registered across your organisation (all roles combined)"
            />
          </S.HeaderLeft>
          <RightIcon onClick={handleNavigation} />
        </S.HeaderRow>

        <S.StatsGrid>
          {showManagersAndCarers && (
            <>
              <S.StatItem>
                <S.IconCircle>
                  <ManagerIcon />
                </S.IconCircle>
                <S.StatContent>
                  <S.StatLabel>
                    <LabelWithTooltip
                      text="Total managers"
                      tooltipId="overview-total-managers"
                      marginLeft={0}
                      tooltipText="Number of users responsible for supervising carers and overseeing care delivery"
                    />
                  </S.StatLabel>
                  <S.CardSubtitle>{totals.managers}</S.CardSubtitle>
                </S.StatContent>
              </S.StatItem>

              <S.StatItem>
                <S.IconCircle>
                  <CarerIcon />
                </S.IconCircle>
                <S.StatContent>
                  <S.StatLabel>
                    <LabelWithTooltip
                      text="Total carers"
                      tooltipId="overview-total-carers"
                      marginLeft={0}
                      tooltipText="Number of people who use the app but don’t manage or supervise others"
                    />
                  </S.StatLabel>
                  <S.CardSubtitle>{totals.carers}</S.CardSubtitle>
                </S.StatContent>
              </S.StatItem>
            </>
          )}

          <S.StatItem>
            <S.IconCircle>
              <AdminIcon />
            </S.IconCircle>
            <S.StatContent>
              <S.StatLabel>
                <LabelWithTooltip
                  text="Total admins"
                  tooltipId="overview-total-admins"
                  marginLeft={0}
                  tooltipText="Number of users managing system access, settings, and organisation configuration"
                />
              </S.StatLabel>
              <S.CardSubtitle>{totals.admins}</S.CardSubtitle>
            </S.StatContent>
          </S.StatItem>

        </S.StatsGrid>
      </S.Card>

      {showServiceUsers && (
      <S.Card>
        <S.HeaderRow>
          <S.HeaderLeft>
            <S.CardSubtitle>Service users</S.CardSubtitle>
            <LabelWithTooltip
              text=""
              tooltipId="overview-service-users"
              tooltipText="Receives support and care from the organisation"
            />
          </S.HeaderLeft>
        </S.HeaderRow>

        <S.StatsGrid>
          <S.StatItem>
            <S.IconCircle>
              <ServiceUserIcon />
            </S.IconCircle>
            <S.StatContent>
              <S.StatLabel>Total service users</S.StatLabel>
              <S.CardSubtitle>{totals.serviceUsers}</S.CardSubtitle>
            </S.StatContent>
          </S.StatItem>
        </S.StatsGrid>
      </S.Card>
      )}
      </S.OverviewCards>
    </S.Container>
  )
}

const mapStateToProps = (state: { activeBot?: any }) => ({
  activeBot: state.activeBot,
})

export default connect(mapStateToProps)(withRouter(Overview))
